import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import {
  ADMIN_PERMISSION_LABELS,
  ALL_ADMIN_PERMISSIONS,
  AdminPermission,
  RoleDto,
} from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import { BaseModalProps } from '../../types/modal'
import TextInput from '../ui/TextInput'

interface RoleModalProps extends BaseModalProps {
  role: RoleDto | null
  onSaved: (role: RoleDto) => void
}

const togglePermission = (
  permissions: AdminPermission[],
  permission: AdminPermission,
): AdminPermission[] => {
  if (permissions.includes(permission)) {
    return permissions.filter(item => item !== permission)
  }
  return [...permissions, permission]
}

const RoleModal = ({ isOpen, onClose, role, onSaved }: RoleModalProps) => {
  const isEdit = role !== null
  const [name, setName] = useState('')
  const [permissions, setPermissions] = useState<AdminPermission[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setName(role?.name ?? '')
    setPermissions(role?.permissions ?? [AdminPermission.Dashboard])
    setError(null)
    setIsSaving(false)
  }, [isOpen, role])

  const handleClose = () => {
    if (isSaving) return
    onClose()
  }

  const canSubmit =
    name.trim().length > 0 &&
    permissions.length > 0

  const handleSubmit = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      setError('Role name is required.')
      return
    }
    if (permissions.length === 0) {
      setError('Select at least one permission.')
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      const saved = isEdit
        ? await adminService.updateRole(role.publicId, {
            name: trimmedName,
            permissions,
          })
        : await adminService.createRole({
            name: trimmedName,
            permissions,
          })
      onSaved(saved)
      onClose()
    } catch {
      setError(isEdit ? `Failed to save role "${trimmedName}".` : 'Failed to create role.')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-popup confirm-popup role-modal"
      overlayClassName="modal-overlay"
      closeTimeoutMS={300}
    >
      <div className="modal-content-wrapper">
        <h2>{isEdit ? 'Edit role' : 'Create role'}</h2>
        {!isEdit && (
          <p style={{ marginTop: 0, opacity: 0.85, fontSize: '0.95rem' }}>
            Define a role name and the admin modules it can access.
          </p>
        )}
        <form className="modal-body-content" onSubmit={e => e.preventDefault()}>
          <TextInput
            required
            type="text"
            label="Name"
            name="roleName"
            value={name}
            onChange={setName}
          />
          <div className="form-group">
            <label>Permissions</label>
            <div className="roles-permissions-grid">
              {ALL_ADMIN_PERMISSIONS.map(permission => (
                <label key={permission} className="roles-permission-item">
                  <input
                    type="checkbox"
                    checked={permissions.includes(permission)}
                    disabled={isSaving}
                    onChange={() => setPermissions(prev => togglePermission(prev, permission))}
                  />
                  <span>{ADMIN_PERMISSION_LABELS[permission]}</span>
                </label>
              ))}
            </div>
          </div>
        </form>
        {error && (
          <div className="page-error" style={{ margin: 0 }}>
            {error}
          </div>
        )}
        <div className="modal-footer-content">
          <button type="button" className="styled-button" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button
            type="button"
            className="styled-button"
            onClick={handleSubmit}
            disabled={isSaving || !canSubmit}
          >
            {isSaving ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save changes' : 'Create role'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default RoleModal
